import { useEffect, useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  FaUsers,
  FaRobot,
  FaCalendarDay,
  FaClock,
  FaDownload,
  FaChartLine,
} from 'react-icons/fa';
import usageReportsApi from '../../../services/usageReportsApi';
import {
  StatCard,
  ChartCard,
  baseBarOptions,
  lineOptions,
  Pagination,
  EmptyState,
} from '../analytics/analyticsShared';

const EVENT_TYPE_LABELS = {
  login: 'Login',
  ai_health_report_generated: 'AI health report generated',
  ai_chat_query: 'AI chat query',
};

function label(eventType) {
  return EVENT_TYPE_LABELS[eventType] || eventType;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

function toCsv(rows) {
  const headers = ['Name', 'Email', 'Role', 'Company', 'Total events', 'AI events', 'Active days', 'First activity', 'Last activity'];
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(
      [r.name, r.email, r.role, r.companyName || '', r.totalEvents, r.aiEvents, r.activeDaysCount, fmtDate(r.firstActivity), fmtDate(r.lastActivity)]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(',')
    );
  }
  return lines.join('\n');
}

// Real, evidence-grade platform & AI usage report — built from UsageEvent
// records that the server writes at the moment a customer logs in, runs an
// AI health report, or sends an AI chat query. Nothing here is backfilled.
function UsageReports() {
  const [summary, setSummary] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [summaryRes, aiRes] = await Promise.all([
          usageReportsApi.getSummary(),
          usageReportsApi.getAiUsage(),
        ]);
        if (mounted) {
          setSummary(summaryRes);
          setAiUsage(aiRes);
        }
      } catch (err) {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openTimeline = async (userId) => {
    setSelectedUser(userId);
    setTimelineLoading(true);
    try {
      const res = await usageReportsApi.getUserTimeline(userId);
      setTimeline(res);
    } catch (err) {
      /* interceptor toasts */
    } finally {
      setTimelineLoading(false);
    }
  };

  const pagedUsers = useMemo(() => {
    if (!summary?.users) return [];
    return summary.users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [summary, page]);

  const trendChartData = useMemo(() => {
    if (!aiUsage?.dailyTrend) return null;
    return {
      labels: aiUsage.dailyTrend.map((d) => d.date),
      datasets: [
        {
          label: 'AI tool uses',
          data: aiUsage.dailyTrend.map((d) => d.count),
          borderColor: '#1e5c33',
          backgroundColor: 'rgba(30,92,51,0.1)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [aiUsage]);

  const byFeatureChartData = useMemo(() => {
    if (!aiUsage?.byFeature) return null;
    return {
      labels: aiUsage.byFeature.map((f) => label(f.eventType)),
      datasets: [
        {
          label: 'Total uses',
          data: aiUsage.byFeature.map((f) => f.totalUses),
          backgroundColor: '#1e5c33',
          borderRadius: 4,
        },
      ],
    };
  }, [aiUsage]);

  const handleExportCsv = () => {
    if (!summary?.users) return;
    const csv = toCsv(summary.users);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-usage-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading usage data…</div>;
  }

  if (!summary || summary.users.length === 0) {
    return (
      <EmptyState
        icon={<FaUsers />}
        title="No recorded usage yet"
        message="This report only shows real activity captured since usage logging was enabled. As customers log in and use the AI tools, their activity will appear here automatically."
      />
    );
  }

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Platform &amp; AI Usage Report</h1>
          <p className="text-xs text-gray-400 mt-1">
            Live activity captured since usage logging was enabled — not backfilled or estimated.
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-primary text-white hover:opacity-90"
        >
          <FaDownload /> Export CSV
        </button>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Customers with activity" value={summary.users.length} icon={<FaUsers />} />
        <StatCard label="Total AI tool uses" value={aiUsage?.totalAiEvents ?? 0} icon={<FaRobot />} />
        <StatCard label="Distinct customers using AI" value={aiUsage?.distinctAiUserCount ?? 0} icon={<FaChartLine />} />
        <StatCard
          label="Report window"
          value={`${new Date(summary.from).toLocaleDateString()} – ${new Date(summary.to).toLocaleDateString()}`}
          icon={<FaCalendarDay />}
        />
      </div>

      {/* AI usage charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="AI tool usage over time" className="h-72">
          <div className="h-56">
            {trendChartData ? <Line data={trendChartData} options={lineOptions} /> : (
              <div className="text-xs text-gray-400 text-center pt-16">No AI usage recorded in this window.</div>
            )}
          </div>
        </ChartCard>
        <ChartCard title="Usage by AI feature" className="h-72">
          <div className="h-56">
            {byFeatureChartData ? <Bar data={byFeatureChartData} options={baseBarOptions} /> : (
              <div className="text-xs text-gray-400 text-center pt-16">No AI usage recorded in this window.</div>
            )}
          </div>
        </ChartCard>
      </div>

      {aiUsage?.byFeature?.length > 0 && (
        <ChartCard title="AI feature breakdown">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-4">Feature</th>
                  <th className="py-2 pr-4">Total uses</th>
                  <th className="py-2 pr-4">Distinct customers</th>
                  <th className="py-2 pr-4">Avg. response time</th>
                </tr>
              </thead>
              <tbody>
                {aiUsage.byFeature.map((f) => (
                  <tr key={f.eventType} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-medium text-gray-700">{label(f.eventType)}</td>
                    <td className="py-2 pr-4">{f.totalUses}</td>
                    <td className="py-2 pr-4">{f.distinctUserCount}</td>
                    <td className="py-2 pr-4">{f.avgDurationMs != null ? `${f.avgDurationMs} ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      {/* Per-customer usage table */}
      <ChartCard title="Customer usage — frequency & duration of platform use">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Total events</th>
                <th className="py-2 pr-4">AI events</th>
                <th className="py-2 pr-4">Active days</th>
                <th className="py-2 pr-4">First activity</th>
                <th className="py-2 pr-4">Last activity</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((u) => (
                <tr key={u.userId} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-700">{u.name}</div>
                    <div className="text-xs text-gray-400">{u.email}</div>
                    {u.companyName && <div className="text-xs text-gray-400">{u.companyName}</div>}
                  </td>
                  <td className="py-2 pr-4 capitalize">{u.role}</td>
                  <td className="py-2 pr-4">{u.totalEvents}</td>
                  <td className="py-2 pr-4">{u.aiEvents}</td>
                  <td className="py-2 pr-4">{u.activeDaysCount}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(u.firstActivity)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(u.lastActivity)}</td>
                  <td className="py-2 pr-4">
                    <button
                      onClick={() => openTimeline(u.userId)}
                      className="text-xs text-primary underline hover:opacity-80"
                    >
                      View log
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} total={summary.users.length} pageSize={PAGE_SIZE} />
      </ChartCard>

      {/* Per-customer activity log (drill-down evidence) */}
      {selectedUser && (
        <ChartCard title="Customer activity log">
          {timelineLoading ? (
            <div className="text-sm text-gray-400">Loading…</div>
          ) : timeline ? (
            <div>
              <div className="text-sm text-gray-600 mb-3">
                <strong>{timeline.user.firstName} {timeline.user.lastName}</strong> — {timeline.eventCount} recorded event(s)
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-4">When</th>
                      <th className="py-2 pr-4">Event</th>
                      <th className="py-2 pr-4">AI feature?</th>
                      <th className="py-2 pr-4">Duration</th>
                      <th className="py-2 pr-4">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.events.map((e) => (
                      <tr key={e._id} className="border-b border-gray-50">
                        <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                        <td className="py-2 pr-4">{label(e.eventType)}</td>
                        <td className="py-2 pr-4">{e.isAiFeature ? 'Yes' : 'No'}</td>
                        <td className="py-2 pr-4">{e.durationMs != null ? `${e.durationMs} ms` : '—'}</td>
                        <td className="py-2 pr-4 text-xs text-gray-500">
                          {e.metadata && Object.keys(e.metadata).length > 0 ? JSON.stringify(e.metadata) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </ChartCard>
      )}

      <div className="text-xs text-gray-400 flex items-center gap-2">
        <FaClock /> Figures reflect activity recorded automatically by the platform in real time. They cannot include usage from before logging was enabled.
      </div>
    </div>
  );
}

export default UsageReports;
